import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiClient } from '@/api/client';
import { useToastStore } from '@/stores/toast-store';

/**
 * Share dialog for connections + tunnels. Reuses one component for both —
 * the resource type only changes the API path. Shows the directory of
 * known users (everyone who has logged into DBGo at least once), the
 * current shares for this resource, and lets the owner add/remove.
 *
 * Cascade rule: when sharing a connection that has a tunnel, the server
 * auto-shares the tunnel too. We surface that as a toast so the owner
 * knows the tunnel went along.
 */

interface ShareDialogProps {
  resourceType: 'connection' | 'tunnel';
  resourceId: string;
  resourceName: string;
  onClose: () => void;
}

interface KnownUser {
  userId: string;
  email: string | null;
  displayName: string | null;
  lastSeenAt: string;
}

interface Share {
  userId: string;
  grantedBy: string;
  grantedAt: string;
  email: string | null;
  displayName: string | null;
}

export function ShareDialog({ resourceType, resourceId, resourceName, onClose }: ShareDialogProps) {
  const [knownUsers, setKnownUsers] = useState<KnownUser[]>([]);
  const [shares, setShares] = useState<Share[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const addToast = useToastStore((s) => s.addToast);

  const basePath = resourceType === 'connection' ? '/api/connections' : '/api/tunnels';

  const refresh = useCallback(async () => {
    setError('');
    try {
      const [users, shareList] = await Promise.all([
        apiClient.get<KnownUser[]>('/api/known-users'),
        apiClient.get<Share[]>(`${basePath}/${resourceId}/shares`),
      ]);
      setKnownUsers(users);
      setShares(shareList);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load shares');
    } finally {
      setLoading(false);
    }
  }, [basePath, resourceId]);

  useEffect(() => { refresh(); }, [refresh]);

  const sharedIds = useMemo(() => new Set(shares.map((s) => s.userId)), [shares]);

  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return knownUsers
      .filter((u) => !sharedIds.has(u.userId))
      .filter((u) => {
        if (!q) return true;
        return (
          (u.displayName ?? '').toLowerCase().includes(q) ||
          (u.email ?? '').toLowerCase().includes(q) ||
          u.userId.toLowerCase().includes(q)
        );
      });
  }, [knownUsers, sharedIds, search]);

  const handleShare = useCallback(async (userId: string) => {
    setSubmitting(userId);
    setError('');
    try {
      const res = await apiClient.post<{ ok: boolean; cascadedTunnel?: string | null }>(
        `${basePath}/${resourceId}/shares`,
        { userId },
      );
      if (resourceType === 'connection' && res.cascadedTunnel) {
        addToast('success', 'Connection shared (tunnel was shared automatically)');
      } else {
        addToast('success', 'Shared');
      }
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to share');
    } finally {
      setSubmitting(null);
    }
  }, [basePath, resourceId, resourceType, refresh, addToast]);

  const handleUnshare = useCallback(async (userId: string) => {
    setSubmitting(userId);
    setError('');
    try {
      const res = await apiClient.delete<{ ok: boolean; dependents?: Array<{ id: string; name: string }> }>(
        `${basePath}/${resourceId}/shares/${encodeURIComponent(userId)}`,
      );
      if (resourceType === 'tunnel' && res.dependents && res.dependents.length > 0) {
        addToast('warning', `Removed. Note: ${res.dependents.length} shared connection(s) using this tunnel will no longer work for them.`);
      } else {
        addToast('success', 'Removed');
      }
      await refresh();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to unshare');
    } finally {
      setSubmitting(null);
    }
  }, [basePath, resourceId, resourceType, refresh, addToast]);

  const labelFor = (u: { displayName: string | null; email: string | null; userId: string }) => {
    return u.displayName || u.email || u.userId;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        className="w-full max-w-[520px] mx-3 bg-dbgo-modal border rounded-lg shadow-2xl flex flex-col max-h-[85vh]"
      >
        <div className="px-5 py-4 border-b border-subtle">
          <h2 className="text-sm font-semibold text-text-primary">
            Share {resourceType === 'connection' ? 'Connection' : 'Tunnel'} — {resourceName}
          </h2>
          <p className="text-xs text-text-muted mt-1">
            Only people who have logged into DBGo at least once appear here.
          </p>
        </div>

        <div className="px-5 py-4 space-y-4 overflow-y-auto">
          {/* Current shares */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              Shared with ({shares.length})
            </label>
            {shares.length === 0 ? (
              <div className="text-xs text-text-muted italic">No shares yet</div>
            ) : (
              <div className="border rounded-sm divide-y divide-subtle">
                {shares.map((s) => (
                  <div key={s.userId} className="flex items-center justify-between px-3 py-2">
                    <div className="min-w-0">
                      <div className="text-[13px] text-text-primary truncate">{labelFor(s)}</div>
                      {s.email && s.displayName && (
                        <div className="text-xs text-text-muted truncate">{s.email}</div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleUnshare(s.userId)}
                      disabled={submitting === s.userId}
                      className="px-2 py-1 text-xs text-accent-red hover:bg-accent-red/10 rounded-sm disabled:opacity-40"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add a share */}
          <div>
            <label className="block text-xs font-semibold text-text-secondary mb-1.5">
              Add someone
            </label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, email, or sub..."
              className="w-full bg-dbgo-input border rounded-sm px-2.5 py-[7px] text-[13px] text-text-primary placeholder:text-text-muted outline-none focus:border-focus transition-colors"
            />
            <div className="mt-2 border rounded-sm max-h-[200px] overflow-y-auto">
              {loading ? (
                <div className="px-3 py-2 text-xs text-text-muted">Loading...</div>
              ) : candidates.length === 0 ? (
                <div className="px-3 py-2 text-xs text-text-muted italic">
                  {knownUsers.length === 0
                    ? 'No other users have used DBGo yet'
                    : search
                      ? 'No matches'
                      : 'Already shared with everyone available'}
                </div>
              ) : (
                <div className="divide-y divide-subtle">
                  {candidates.map((u) => (
                    <button
                      key={u.userId}
                      type="button"
                      onClick={() => handleShare(u.userId)}
                      disabled={submitting === u.userId}
                      className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-dbgo-hover disabled:opacity-40"
                    >
                      <div className="min-w-0">
                        <div className="text-[13px] text-text-primary truncate">{labelFor(u)}</div>
                        {u.email && u.displayName && (
                          <div className="text-xs text-text-muted truncate">{u.email}</div>
                        )}
                      </div>
                      <span className="text-xs text-accent-blue">Share</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {error && <div className="text-xs text-accent-red">{error}</div>}
        </div>

        <div className="px-5 py-3 border-t border-subtle flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-xs text-text-secondary hover:text-text-primary transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
